import { FormikContextType } from "formik"
import type { TFunction } from "@/lib/i18n"

/**
 * Resolve a Formik field error to display text.
 *
 * Field errors come from two places with different shapes:
 *   - Yup schemas / `setFieldError` calls in code → translation keys
 *     (`profile:form_errors.required`, `investor:...`) that must run through
 *     `t()` to render.
 *   - `mapRestErrorsToFormik` / `mapGraphQLErrorsToFormik` → pre-translated
 *     free text from the backend (dots, colons, spaces). i18next would parse
 *     the `:` / `.` as ns/key separators and truncate the sentence.
 *
 * `defaultValue: rawError` covers both: i18next returns the resolved
 * translation when the key exists, and falls back to the original string
 * verbatim when it doesn't — so unprefixed keys still resolve via the caller's
 * default namespace and free-text sentences render untouched.
 */
export function resolveFieldError(
	t: TFunction,
	rawError: unknown,
): string | null {
	if (typeof rawError !== "string" || !rawError) return null
	return t(rawError, { defaultValue: rawError })
}

export function handleErrorWithFormik(
	error: Error,
	form: FormikContextType<any>,
) {
	if (error?.message === "Failed to fetch") {
		form.setFieldError("errors.common", "common:form_errors.network_error")
		return
	}
	if (typeof error?.message === "string") {
		form.setFieldError("errors.common", "common:form_errors.internal_error")
		return
	}
	form.setFieldError("errors.common", "common:form_errors.unknown_error")
}

/**
 * Map a DRF-style error body ({ field: ["message", ...] }) onto a Formik form.
 * `src/api/rest/api.ts` throws the parsed JSON body for 4xx responses, so this
 * is usually called from a catch block with the thrown value.
 *
 * Only keys that also exist in `form.values` are mapped — this avoids setting
 * errors on unrelated top-level keys like `detail` or `non_field_errors`.
 * Also calls setFieldTouched so inline <FormikError /> renders (it short-
 * circuits when the field is untouched).
 *
 * Returns true if at least one field was mapped, so callers can decide whether
 * to fall back to a generic toast / Sentry report.
 */
export function mapRestErrorsToFormik(
	error: unknown,
	form: FormikContextType<any>,
): boolean {
	if (!error || typeof error !== "object") return false
	const body = error as Record<string, unknown>
	let mapped = false
	Object.keys(form.values).forEach((field) => {
		const val = body[field]
		const message = Array.isArray(val) ? val[0] : val
		if (typeof message === "string" && message.length > 0) {
			void form.setFieldTouched(field, true, false)
			form.setFieldError(field, message)
			mapped = true
		}
	})
	return mapped
}

/**
 * Walk a Formik `values` object and produce the set of dot-notation paths that
 * can safely receive a field error. Nested plain objects are recursed into;
 * arrays are treated as leaves (their indices aren't fields). Every key along
 * the way is included, so a shape like { me: { profile: { city: "" } } }
 * yields { "me", "me.profile", "me.profile.city" } — this lets the backend
 * target either the leaf or a wrapping node.
 */
function collectFieldPaths(values: unknown, prefix = ""): Set<string> {
	const paths = new Set<string>()
	if (!values || typeof values !== "object" || Array.isArray(values)) {
		return paths
	}
	for (const [key, child] of Object.entries(
		values as Record<string, unknown>,
	)) {
		const path = prefix ? `${prefix}.${key}` : key
		paths.add(path)
		if (child && typeof child === "object" && !Array.isArray(child)) {
			for (const nested of collectFieldPaths(child, path)) {
				paths.add(nested)
			}
		}
	}
	return paths
}

/**
 * Map a GraphQL mutation's `errors` array onto a Formik form.
 *
 * The backend returns errors on mutation payloads as
 * `Array<{ field: string, messages: string[] } | null> | null`, exported as
 * `ErrorType` from `@/api/graphql`. Callers pass the array itself, e.g.
 * `mapGraphQLErrorsToFormik(response?.foo_mutation?.errors, form)`, so the
 * helper stays agnostic of the mutation-specific payload key.
 *
 * Both flat (`iban`) and dotted (`me.profile.address.city`) field paths are
 * supported — the check walks the shape of `form.values`, so an error only
 * maps if the path actually exists on the form. This prevents leakage of
 * unrelated keys like `nonFieldErrors` (handle those explicitly in the caller
 * if needed).
 *
 * Returns true if at least one field was mapped, so callers can decide whether
 * to fall back to a generic error toast / Sentry report / errors.common.
 */
export function mapGraphQLErrorsToFormik(
	errors:
		| ReadonlyArray<{ field?: string; messages?: string[] } | null>
		| null
		| undefined,
	form: FormikContextType<any>,
): boolean {
	if (!errors || errors.length === 0) return false
	const formFields = collectFieldPaths(form.values)
	let mapped = false
	for (const err of errors) {
		if (!err?.field || !formFields.has(err.field)) continue
		const message = err.messages?.[0]
		if (typeof message !== "string" || message.length === 0) continue
		void form.setFieldTouched(err.field, true, false)
		form.setFieldError(err.field, message)
		mapped = true
	}
	return mapped
}

// Re-export formik
export { FormikProvider, useFormik } from "formik"
